import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { SiteCtx } from "@/lib/lang";
import defaultContent from "@/lib/content";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import LegalPage from "@/pages/LegalPage";
import MacroareaPage from "@/pages/MacroareaPage";
import TargetPage from "@/pages/TargetPage";
import OsservatorioPage from "@/pages/OsservatorioPage";
import AdminApp from "@/pages/admin/AdminApp";
import NotFound from "@/pages/NotFound";
import "@/App.css";

// Dominio canonico pubblico definitivo (SEO). Stabile e indipendente da REACT_APP_BACKEND_URL
// (che serve solo per le chiamate API). Usato per canonical / og:url / immagini assolute.
const SITE_URL = "https://ethicenergy.it";

function RouteSeo({ content, lang }) {
  const location = useLocation();
  useEffect(() => {
    const pick = (f) => (typeof f === "string" ? f : f?.[lang] ?? f?.it);
    const seo = content.seo || {};
    let title = pick(seo.title) || null;
    let desc = pick(seo.description) || null;
    let ogImage = null;
    const m = location.pathname.match(/^\/soluzioni\/([\w-]+)/);
    if (m && content.macroaree?.[m[1]]) {
      const a = content.macroaree[m[1]];
      const at = pick(a.seo_title) || pick(a.title);
      if (at) title = at.includes("ETHIC") ? at : `${at} | ETHIC ENERGY`;
      desc = pick(a.seo_desc) || pick(a.intro) || desc;
      ogImage = `${SITE_URL}/og-${m[1]}.png`;
    }
    const t = location.pathname.match(/^\/clienti\/([\w-]+)/);
    if (t && Array.isArray(content.target)) {
      const seg = content.target.find((x) => x.id === t[1]);
      if (seg) {
        const st = pick(seg.seo_title) || pick(seg.label);
        if (st) title = st.includes("ETHIC") ? st : `${st} | ETHIC ENERGY`;
        desc = pick(seg.seo_desc) || pick(seg.intro) || desc;
        ogImage = `${SITE_URL}/og-clienti-${t[1]}.png`;
      }
    }
    if (location.pathname === "/osservatorio-energia") {
      title = "Osservatorio Energia | ETHIC ENERGY";
      desc = "Prezzi aggiornati di energia elettrica (PUN) e gas (PSV) in Italia: andamento recente, media mensile e storico.";
    }
    if (location.pathname === "/privacy-policy") {
      title = "Privacy Policy | ETHIC ENERGY";
      desc = "Informativa sul trattamento dei dati personali di ETHIC ENERGY, business unit di RR Solutions srl.";
    }
    if (location.pathname === "/cookie-policy") {
      title = "Cookie Policy | ETHIC ENERGY";
      desc = "Informativa sui cookie utilizzati dal sito ETHIC ENERGY: cookie tecnici e gestione delle preferenze.";
    }
    if (title) document.title = title;
    if (desc) document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
    const setMeta = (attr, key, value) => {
      if (!value) return;
      let meta = document.querySelector(`meta[${attr}="${key}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, key);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", value);
    };
    // Canonical assoluto e specifico per ogni route (dominio pubblico definitivo)
    const canonicalUrl = SITE_URL + (location.pathname === "/" ? "/" : location.pathname);
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonicalUrl);
    if (!ogImage) ogImage = `${SITE_URL}/og-image.png`;
    if (title) setMeta("property", "og:title", title);
    if (desc) setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", ogImage);
    if (title) setMeta("name", "twitter:title", title);
    if (desc) setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", ogImage);
    const kw = pick(seo.keywords);
    if (kw) {
      let meta = document.querySelector('meta[name="keywords"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "keywords";
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", kw);
    }
  }, [location.pathname, content, lang]);
  return null;
}

export default function App() {
  const [content, setContent] = useState(defaultContent);
  const [lang, setLang] = useState("it");

  useEffect(() => {
    api
      .get("/content")
      .then((r) => {
        const { key, ...data } = r.data;
        setContent((prev) => ({ ...prev, ...data }));
      })
      .catch(() => {});
  }, []);

  const ctx = useMemo(() => ({ content, lang, setLang: changeLang }), [content, lang]);

  async function changeLang(l) {
    if (l === "en") {
      const needsTranslation = !content?.hero?.subtitle?.en || !content?.hero?.line1?.en;
      const t = needsTranslation ? toast.loading("Generazione versione inglese in corso…") : null;
      try {
        const r = await api.post("/content/translate");
        const { key, ...data } = r.data;
        setContent((prev) => ({ ...prev, ...data }));
        if (t) toast.success("Versione inglese pronta", { id: t });
      } catch {
        if (t) toast.dismiss(t);
      }
    }
    setLang(l);
  }

  return (
    <SiteCtx.Provider value={ctx}>
      <div className="App bg-onyx text-ice min-h-screen">
        <BrowserRouter>
          <RouteSeo content={content} lang={lang} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
            <Route path="/cookie-policy" element={<LegalPage type="cookie" />} />
            <Route path="/soluzioni/:id" element={<MacroareaPage />} />
            <Route path="/clienti/:id" element={<TargetPage />} />
            <Route path="/osservatorio-energia" element={<OsservatorioPage />} />
            <Route path="/admin" element={<AdminApp />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="light" position="bottom-right" />
        <div className="noise-overlay" />
      </div>
    </SiteCtx.Provider>
  );
}
